import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Container, Row, Col, Card, CardBody, Form, FormGroup, Label, Input, Button, Alert,
} from 'reactstrap';
import { formatPhoneInput, isValidUsPhone, toE164 } from 'util/phoneFormat';
import { getSpecialtyGradient } from 'util/specialtyTheme';
import { SPECIALTIES, DEFAULT_SPECIALTY } from 'config/specialtyConfig';

const FreeAnkiDeck = () => {
  const { specialty } = useParams();
  const specialtyId = SPECIALTIES[specialty] ? specialty : DEFAULT_SPECIALTY;
  const specialtyName = SPECIALTIES[specialtyId].name;
  const pageBackground = { background: getSpecialtyGradient(specialtyId), minHeight: '100vh' };
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!isValidUsPhone(form.phone)) {
      setError('Please enter a complete 10-digit phone number.');
      return;
    }

    setSubmitting(true);

    try {
      // Public endpoint — bare axios instance so no auth header is attached.
      // Phone is sent as E.164 so SMS retargeting works without cleanup.
      const publicAxios = axios.create();
      await publicAxios.post(axios.defaults.baseURL + '/claimAnkiDeck', {
        ...form,
        phone: toE164(form.phone),
        // Submitting the form IS the consent action; the disclosure sits
        // beside the button. Recorded server-side with a timestamp.
        smsConsent: true,
        specialty: specialtyId,
      });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="py-8" style={pageBackground}>
        <Container>
          <Row className="justify-content-center">
            <Col lg="6">
              <Card className="shadow">
                <CardBody className="text-center py-5">
                  <i className="fas fa-envelope-open-text fa-3x text-success mb-3" />
                  <h2>Check your inbox</h2>
                  <p className="text-muted mb-0">
                    We've sent the deck to <strong>{form.email}</strong>. It should arrive within
                    a few minutes — check your spam folder if you don't see it.
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="py-8" style={pageBackground}>
      <Container>
        <Row className="justify-content-center">
          <Col lg="9">
            <Card className="shadow">
              <CardBody className="p-5">
                <Row>
                  <Col md="6" className="mb-4 mb-md-0">
                    <h1 className="display-4 mb-3">Free {specialtyName} Anki Deck</h1>
                    <p className="lead text-muted">
                      High-yield flashcards covering the {specialtyName} board content, using
                      spaced repetition so the material actually sticks.
                    </p>
                    <ul className="list-unstyled">
                      <li className="mb-2"><i className="fas fa-check text-success mr-2" />Mapped to the board content outline</li>
                      <li className="mb-2"><i className="fas fa-check text-success mr-2" />Works in the free Anki app</li>
                      <li className="mb-2"><i className="fas fa-check text-success mr-2" />Study on desktop or mobile</li>
                    </ul>
                  </Col>
                  <Col md="6">
                    <h4 className="mb-3">Claim your copy</h4>
                    {error && <Alert color="danger">{error}</Alert>}
                    <Form onSubmit={handleSubmit}>
                      <FormGroup>
                        <Label>First name *</Label>
                        <Input
                          type="text"
                          value={form.firstName}
                          onChange={(e) => setField('firstName', e.target.value)}
                          required
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Last name</Label>
                        <Input
                          type="text"
                          value={form.lastName}
                          onChange={(e) => setField('lastName', e.target.value)}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          value={form.email}
                          onChange={(e) => setField('email', e.target.value)}
                          required
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Phone number *</Label>
                        <Input
                          type="tel"
                          placeholder="(555) 123-4567"
                          value={form.phone}
                          onChange={(e) => setField('phone', formatPhoneInput(e.target.value))}
                          required
                        />
                      </FormGroup>
                      <Button color="primary" block type="submit" disabled={submitting}>
                        {submitting ? 'Sending…' : 'Send me the deck'}
                      </Button>
                      {/* Submitting is the consent action, so this disclosure has to
                          stay visible beside the button. Carrier-rate and STOP wording
                          was removed at the client's request — may need restoring if
                          A2P 10DLC campaign registration asks for it. */}
                      <small className="text-muted d-block mt-3">
                        By clicking "Send me the deck" you agree to receive emails and text
                        messages from PM&amp;R Prep.
                      </small>
                    </Form>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default FreeAnkiDeck;
